import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subject, takeUntil } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { GlobalDropdownComponent } from '../global-dropdown/global-dropdown.component';
import { DateRangeFilterComponent } from '../date-range-filter/date-range-filter.component';
import {
  collectDependencyValues,
  dependencySignature,
  resolveDependencyKeys,
} from '../../utils/dropdown-dependency.utils';

/**
 * One row of the group: a black label cell on the left, a gray dropdown
 * value cell on the right (Regional Price Files mockup style).
 */
export interface LabeledDropdownField {
  label: string;
  key: string;
  /**
   * `dropdown` (default) renders a local ng-select styled like the shared
   * multi-select. `date` delegates to the date widget.
   */
  type?: 'dropdown' | 'date';
  /** Enables search, select all, checkboxes, and multi-value selection. */
  isMultiSelect?: boolean;
  /** Static options; when apiUrl is set these are ignored. */
  options?: string[];
  /** Option-loading contract identical to global-dropdown. */
  apiUrl?: string;
  method?: string;
  responseDataKey?: string;
  skipDefaultPayload?: boolean;
  customPayload?: unknown;
  isQueryParam?: boolean;
  appendTo?: string;
  disabled?: boolean;
  /** Marks the field as mandatory in its label. Enforcement belongs to the consuming action. */
  required?: boolean;
  placeholder?: string;
  disabledPlaceholder?: string;
  /** Preselected value shown before the user picks one. */
  value?: string | string[];
  /**
   * Global-filter keys this field's option list is narrowed by. Declaring them
   * makes the field re-request its options whenever one of those selections
   * changes, sending their current values as `requestData`. Give every field in
   * a group the same key list so one selection produces one shared request.
   */
  dependsOn?: string | string[];
  /** Send selected dependencies as getData filterList rules, not requestData. */
  dependenciesAsFilterList?: boolean;
  /** Dependency keys sent as a single scalar instead of an array. */
  scalarFilterKeys?: string[];
  /** Optional alphabetical ordering applied after dropdown options are loaded. */
  sortOptions?: 'asc' | 'desc';
  /** Keep the initially loaded option domain when page filters change. */
  reloadOptionsOnFilterChange?: boolean;
  /** Passed through to the delegated widgets. */
  [extra: string]: unknown;
}

export interface LabeledDropdownGroupConfig {
  fields?: LabeledDropdownField[];
  /** Optional accent used by the label cells and dropdown carets. */
  accentColor?: string;
  /** Label cell width, e.g. "110px". */
  labelWidth?: string;
  /** Row height, e.g. "22px". */
  rowHeight?: string;
}

type FieldKind = 'single' | 'multi' | 'date';

interface FieldState {
  config: LabeledDropdownField;
  kind: FieldKind;
  options: string[];
  value: string | string[] | null;
  /** Dependency snapshot the current option list was requested with. */
  dependencySignature: string | null;
}

/**
 * A compact stack of label+dropdown rows styled like the Regional Price Files
 * spec (black caption cell, gray value cell, caret).
 * Selections publish through GlobalFilterService exactly like global-dropdown,
 * so buttons/tables on the page react the same way.
 */
@Component({
  selector: 'app-labeled-dropdown-group',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgSelectModule,
    GlobalDropdownComponent,
    DateRangeFilterComponent,
  ],
  templateUrl: './labeled-dropdown-group.component.html',
  styleUrl: './labeled-dropdown-group.component.scss',
})
export class LabeledDropdownGroupComponent implements OnInit, OnDestroy {
  @Input() data: LabeledDropdownGroupConfig = {};

  fields: FieldState[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
  ) {}

  ngOnInit(): void {
    this.fields = (this.data?.fields ?? []).map((config) => ({
      // The rows are stacked and their value cells are opaque, so a panel
      // rendered inline gets painted over by the rows below it - no z-index on
      // the open row can fix that, since lifting the row makes it a stacking
      // context that traps the panel inside. Send the panel to a body-level
      // overlay instead, where nothing in the group can cover or clip it.
      // A field may still override this with its own `appendTo`.
      config: {
        name: config.label,
        title: config.label,
        appendTo: 'body',
        ...config,
        // Compact group rows display selections as field text while retaining
        // ng-select's clear control for enabled fields.
        clearable: config.disabled !== true,
        plainTextLabel: true,
      },
      kind: this.resolveKind(config),
      options: Array.isArray(config.options) ? [...config.options] : [],
      value: this.normalizeConfiguredValue(config.value, config.isMultiSelect === true),
      dependencySignature: null,
    }));

    // Navigation state is seeded before dynamically-created widgets subscribe.
    // GlobalFilterService uses a Subject, so restore its current snapshot too.
    // This runs before the first option load so a `dependsOn` field requests
    // its list already narrowed by whatever the page arrived with.
    this.syncFromFilters(this.globalFilterService.getCurrentFilters?.());

    // The local single-select owns its loading and filter synchronization.
    // Multi-select/date fields continue delegating to their existing widgets.
    this.ownFields.forEach((field) => this.loadOptions(field));

    this.ownFields
      .filter((field) => field.value)
      .forEach((field) => this.publish(field, false));

    this.globalFilterService.filter$
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters) => {
        this.syncFromFilters(filters);
        // Re-request the option lists of fields that declare `dependsOn`.
        // loadOptions no-ops when a field's dependency snapshot is unchanged,
        // so fields without `dependsOn` still load exactly once.
        this.ownFields
          .filter((field) => field.config.reloadOptionsOnFilterChange !== false)
          .forEach((field) => this.loadOptions(field));
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get ownFields(): FieldState[] {
    return this.fields.filter((field) => field.kind === 'single');
  }

  private resolveKind(config: LabeledDropdownField): FieldKind {
    if (config.type === 'date') return 'date';
    return config.isMultiSelect ? 'multi' : 'single';
  }

  get labelWidth(): string {
    return this.data?.labelWidth || '110px';
  }

  get rowHeight(): string | null {
    return this.data?.rowHeight || null;
  }

  onValueChange(field: FieldState, value: unknown): void {
    field.value = value === null || value === undefined || value === '' ? null : String(value);
    this.updateConfiguredValue(field, field.value);
    this.publish(field, true);
  }

  onSingleOptionClick(field: FieldState, item: unknown, event: Event): void {
    if (field.value !== String(item)) return;

    // ng-select keeps the current value when a selected single option is
    // clicked. Intercept that click so the dropdown itself can also unselect
    // the value without exposing a separate clear icon in the field.
    event.preventDefault();
    event.stopPropagation();
    this.onValueChange(field, null);
  }

  /**
   * Multi-selects continue publishing through GlobalDropdownComponent. Mirror
   * its emitted array into the labeled-group JSON model so removing a chip or
   * deselecting an option is reflected immediately without a second publish.
   */
  onMultiValueChange(field: FieldState, value: unknown): void {
    const selected = Array.isArray(value) ? [...value] : [];
    field.value = selected;
    this.updateConfiguredValue(field, selected);
  }

  trackByKey(_i: number, field: FieldState): string {
    return field.config.key;
  }

  private publish(field: FieldState, emit: boolean): void {
    const key = String(field.config.key || '').trim();
    if (!key) return;
    const values = typeof field.value === 'string' && field.value ? [field.value] : [];
    this.globalFilterService.updateFilterSelection(
      key,
      values,
      field.config.isQueryParam,
      undefined,
      emit,
    );
  }

  private syncFromFilters(filters: any): void {
    for (const field of this.ownFields) {
      const key = String(field.config.key || '').trim();
      if (!key) continue;
      const bag = field.config.isQueryParam ? filters?.queryParams : filters?.requestParams;
      const raw = bag?.[key];
      const value = Array.isArray(raw) ? raw[0] : raw;
      field.value = value === null || value === undefined || value === '' ? null : String(value);
      this.updateConfiguredValue(field, field.value);
    }
  }

  private normalizeConfiguredValue(value: unknown, isMultiSelect: boolean): string | string[] | null {
    if (isMultiSelect) {
      return Array.isArray(value)
        ? value.filter((item) => item !== null && item !== undefined).map(String)
        : value === null || value === undefined || value === ''
          ? []
          : [String(value)];
    }
    return value === null || value === undefined || value === '' ? null : String(value);
  }

  private updateConfiguredValue(field: FieldState, value: string | string[] | null): void {
    const normalized = Array.isArray(value) ? [...value] : value;
    field.config.value = normalized ?? undefined;
    const source = this.data?.fields?.find((item) => item.key === field.config.key);
    if (source) {
      source.value = normalized ?? undefined;
    }
  }

  private loadOptions(field: FieldState): void {
    const apiUrl = String(field.config.apiUrl || '').trim();
    if (!apiUrl) return;

    const dependencyKeys = resolveDependencyKeys(field.config);
    const dependencyValues = collectDependencyValues(
      this.globalFilterService.getCurrentFilters?.(),
      dependencyKeys,
      field.config.scalarFilterKeys,
    );

    // Constant for a field without `dependsOn`, so it is requested once and
    // every later filter emission is a no-op.
    const signature = dependencySignature(dependencyValues);
    if (field.dependencySignature === signature) return;
    field.dependencySignature = signature;

    const filters = dependencyKeys.length ? { requestParams: dependencyValues } : undefined;

    this.configService
      .getConfig(apiUrl, field.config, filters, field.config.method)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          field.options = this.resolveResponseOptions(field, res);
        },
        error: (err: unknown) =>
          console.error(`labeled-dropdown-group: options failed for ${field.config.key}`, err),
      });
  }

  private resolveResponseOptions(field: FieldState, response: any): string[] {
    const responseKey = String(field.config.responseDataKey || field.config.key || '').trim();
    const source = Array.isArray(response?.data)
      ? response.data
      : responseKey && response?.[responseKey] !== undefined
        ? response[responseKey]
        : [];

    if (!Array.isArray(source)) return [];

    const options = source
      .flat(Infinity)
      .flatMap((value: unknown) => (typeof value === 'string' ? value.split(',') : [value]))
      .filter((value: unknown) => value !== null && value !== undefined)
      .map((value: unknown) => String(value).trim())
      .filter(Boolean);

    const uniqueOptions = [...new Set(options)];
    const sortDirection = field.config.sortOptions;
    if (sortDirection === 'asc' || sortDirection === 'desc') {
      const direction = sortDirection === 'asc' ? 1 : -1;
      uniqueOptions.sort((left, right) =>
        direction * left.localeCompare(right, undefined, {
          numeric: true,
          sensitivity: 'base',
        }),
      );
    }

    return uniqueOptions;
  }

}
